import React from "react";
import { classMgmtService } from "services/admin/classMgmtService";
import { userService } from "services/admin/userService";
import type {
  ClassCreate,
  ClassItem,
  ClassStatus,
  UserListItem,
} from "types/domain";
import MultiSelectField, {
  type MultiSelectOption,
} from "components/fields/MultiSelectField";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";

interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (created: ClassItem) => void;
  centerId?: string;
}

const STATUSES: ClassStatus[] = ["OPENING", "ONGOING", "CLOSED"];
const STATUS_LABEL_KEY: Record<ClassStatus, string> = {
  OPENING: "enum.classStatus.OPENING",
  ONGOING: "enum.classStatus.ONGOING",
  CLOSED: "enum.classStatus.CLOSED",
};

const INITIAL_FORM: ClassCreate = {
  name: "",
  description: "",
  manager_ids: [],
  status: "OPENING",
};

function CreateClassModal({
  isOpen,
  onClose,
  onCreated,
  centerId,
}: CreateClassModalProps) {
  const { t } = useLanguage();
  const [form, setForm] = React.useState<ClassCreate>(INITIAL_FORM);
  const [classTeachers, setClassTeachers] = React.useState<UserListItem[]>([]);
  const [loadingTeachers, setLoadingTeachers] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const loadTeachers = async () => {
      setLoadingTeachers(true);
      try {
        const [teacherRes, managerRes] = await Promise.all([
          userService.getAll({
            role: "TEACHER",
            center_id: centerId,
            limit: 200,
          }),
          userService.getAll({
            role: "MANAGER",
            center_id: centerId,
            limit: 200,
          }),
        ]);
        const merged = new Map<string, any>();
        [...teacherRes.items, ...managerRes.items].forEach((u) =>
          merged.set(u.id, u)
        );
        setClassTeachers(Array.from(merged.values()));
      } catch (err: any) {
        setError(err.message ?? t("classForm.loadManagersFailed"));
        setClassTeachers([]);
      } finally {
        setLoadingTeachers(false);
      }
    };

    loadTeachers().catch(() => {});
  }, [centerId, isOpen]);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const managerOptions = React.useMemo<MultiSelectOption[]>(
    () =>
      classTeachers.map((teacher) => ({
        value: String(teacher.id),
        label: teacher.full_name,
        description: teacher.email ?? undefined,
      })),
    [classTeachers]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (!form.name?.trim()) {
        throw new Error(t("classForm.nameRequired"));
      }
      const created = await classMgmtService.create({
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        center_id: centerId,
        manager_ids: form.manager_ids,
        status: form.status,
      });
      resetForm();
      onCreated(created);
      onClose();
    } catch (err: any) {
      setError(err.message ?? t("classForm.createFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={handleBackdrop}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-ink">
            {t("classForm.createTitle")}
          </h3>
          <button
            onClick={handleClose}
            className="rounded-lg p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("classForm.nameLabel")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                name="name"
                value={form.name ?? ""}
                onChange={handleChange}
                required
                placeholder={t("classForm.namePlaceholder")}
                className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
              />
            </div>

            <div className="sm:col-span-2">
              <MultiSelectField
                label={t("enum.role.TEACHER")}
                loading={loadingTeachers}
                options={managerOptions}
                values={form.manager_ids ?? []}
                onChange={(nextValues) =>
                  setForm((prev) => ({ ...prev, manager_ids: nextValues }))
                }
                placeholder={t("classForm.searchNameOrEmail")}
                emptyText={t("classForm.noMatchingResults")}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("classForm.statusLabel")}
              </label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted"
              >
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(STATUS_LABEL_KEY[status])}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("classForm.descriptionLabel")}
              </label>
              <textarea
                name="description"
                value={form.description ?? ""}
                onChange={handleChange}
                rows={3}
                placeholder={t("classForm.descriptionPlaceholder")}
                className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <ActionButton
              type="button"
              variant="secondary"
              onClick={handleClose}
            >
              {t("action.cancel")}
            </ActionButton>
            <ActionButton type="submit" variant="primary" disabled={submitting}>
              {submitting
                ? t("classForm.creating")
                : t("classForm.createSubmit")}
            </ActionButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateClassModal;
